import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { exhaustMap, filter, map, of, tap } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { APP_ROUTES } from '@ps/core-config';

import { retryServerError } from '../../utils/retry-server-error.operator';
import { AuthService } from '../../services/auth.service';
import { AuthPasswordActions } from './auth-password.actions';
import { selectUser } from '../auth-session/auth-session.selectors';

const RETRY = {
  count: 3,
  delay: 500,
};

@Injectable()
export class AuthPasswordEffects extends EffectsBase {
  private readonly _store = inject(Store);
  private readonly _actions$ = inject(Actions);
  private readonly _authService = inject(AuthService);
  private readonly _toastService = inject(ToastService);
  private readonly _router = inject(Router);

  public forgotPassword$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthPasswordActions.forgotPassword),
      exhaustMap(({ email, captcha }) =>
        this._authService.forgotPassword(email, captcha).pipe(
          retryServerError(RETRY.count, RETRY.delay),
          map((message) =>
            AuthPasswordActions.forgotPasswordSuccess({ message }),
          ),
          this.psCatchError(
            (error) =>
              of(
                AuthPasswordActions.forgotPasswordFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public forgotPasswordSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthPasswordActions.forgotPasswordSuccess),
        tap(({ message }) => {
          this._toastService.success(message);
          this._router.navigate([APP_ROUTES.LOGIN]);
        }),
      ),
    { dispatch: false },
  );

  public resetPassword$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthPasswordActions.resetPassword),
      concatLatestFrom(() => this._store.select(selectUser)),
      filter(([, user]) => !!user),
      exhaustMap(([credentials, user]) => {
        return this._authService.resetPassword(user!.id, credentials).pipe(
          retryServerError(RETRY.count, RETRY.delay),
          map((message) =>
            AuthPasswordActions.resetPasswordSuccess({ message }),
          ),
          this.psCatchError(
            (error) =>
              of(
                AuthPasswordActions.resetPasswordFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        );
      }),
    ),
  );

  public resetPasswordSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthPasswordActions.resetPasswordSuccess),
        tap(({ message }) => this._toastService.success(message)),
      ),
    { dispatch: false },
  );

  public setPassword$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthPasswordActions.setPassword),
      exhaustMap((credentials) =>
        this._authService.setPassword(credentials).pipe(
          retryServerError(RETRY.count, RETRY.delay),
          map((message) => AuthPasswordActions.setPasswordSuccess({ message })),
          this.psCatchError(
            (error) =>
              of(
                AuthPasswordActions.setPasswordFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public setPasswordSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthPasswordActions.setPasswordSuccess),
        tap(({ message }) => {
          this._toastService.success(message);
          this._router.navigate([APP_ROUTES.LOGIN]);
        }),
      ),
    { dispatch: false },
  );
}
